(() => {
  const SELECTORS = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    pagination: '.pagination-wrapper',
    nextLink: '.pagination-wrapper a[href]',
    controls: '[data-infinite-scroll-controls]',
    loader: '[data-infinite-scroll-loader]',
    button: '[data-infinite-scroll-button]',
    status: '[data-infinite-scroll-status]'
  };

  class DawnHybridInfiniteScroll {
    constructor() {
      this.observer = null;
      this.loading = false;
      this.autoStopped = false;
      this.restoreInProgress = false;

      document.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('click', this.handleProductClick.bind(this), true);
      window.addEventListener('pagehide', this.saveState.bind(this));
      window.addEventListener('beforeunload', this.saveState.bind(this));

      this.init({ allowRestore: true });
    }

    get container() { return document.querySelector(SELECTORS.container); }
    get grid() { return this.container?.querySelector(SELECTORS.grid) || null; }
    get controls() { return this.container?.querySelector(SELECTORS.controls) || null; }
    get loader() { return this.controls?.querySelector(SELECTORS.loader) || null; }
    get button() { return this.controls?.querySelector(SELECTORS.button) || null; }
    get status() { return this.controls?.querySelector(SELECTORS.status) || null; }
    get enabled() { return this.grid?.dataset.infiniteScrollEnabled === 'true'; }
    get maxAutoProducts() { return Number(this.grid?.dataset.infiniteScrollMaxAuto || 192); }
    get loadedCount() { return this.grid?.querySelectorAll(':scope > li').length || 0; }
    get nextUrl() { return this.grid?.dataset.nextUrl || ''; }
    set nextUrl(value) { if (this.grid) this.grid.dataset.nextUrl = value || ''; }

    get storageKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `dawn-hybrid-scroll:${url.pathname}?${url.searchParams.toString()}`;
    }

    init({ allowRestore = false } = {}) {
      this.disconnectObserver();
      this.loading = false;
      this.autoStopped = false;

      if (!this.container || !this.grid || !this.enabled || !this.controls) return;

      this.hideNativePagination();
      this.updateControls();

      if (allowRestore) this.restoreState();
      else this.createObserver();
    }

    reset() {
      try { sessionStorage.removeItem(this.storageKey); } catch (error) {}
      this.init({ allowRestore: false });
    }

    hideNativePagination() {
      const pagination = this.container.querySelector(SELECTORS.pagination);
      if (pagination) {
        pagination.hidden = true;
        pagination.setAttribute('aria-hidden', 'true');
      }
    }

    updateControls() {
      if (!this.controls) return;

      if (!this.nextUrl) {
        this.controls.hidden = true;
        this.disconnectObserver();
        return;
      }

      this.controls.hidden = false;

      if (this.loadedCount >= this.maxAutoProducts) {
        this.autoStopped = true;
        this.disconnectObserver();
        if (this.button) this.button.hidden = false;
        if (this.loader) this.loader.hidden = true;
        if (this.status) this.status.textContent = '';
      } else {
        this.autoStopped = false;
        if (this.button) this.button.hidden = true;
        if (this.loader) this.loader.hidden = false;
      }
    }

    createObserver() {
      if (!this.loader || !this.nextUrl || this.autoStopped || this.restoreInProgress) return;

      this.disconnectObserver();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage({ automatic: true });
          }
        },
        { rootMargin: '700px 0px' }
      );
      this.observer.observe(this.loader);
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    findNextUrl(parsedDocument, currentUrl) {
      const parsedGrid = parsedDocument.querySelector(SELECTORS.grid);
      if (parsedGrid?.dataset.nextUrl) return parsedGrid.dataset.nextUrl;

      const current = new URL(currentUrl, window.location.origin);
      const currentPage = Number(current.searchParams.get('page') || 1);
      const links = [...parsedDocument.querySelectorAll(SELECTORS.nextLink)];
      const next = links.find((link) => {
        const linkUrl = new URL(link.href, window.location.origin);
        return Number(linkUrl.searchParams.get('page') || 1) === currentPage + 1;
      });
      return next?.href || '';
    }

    async loadNextPage({ automatic = false, restoring = false } = {}) {
      if (this.loading || !this.nextUrl) return;

      if (automatic && this.loadedCount >= this.maxAutoProducts) {
        this.updateControls();
        return;
      }

      this.loading = true;
      this.controls?.classList.add('is-loading');
      if (this.button) this.button.disabled = true;
      if (this.status) this.status.textContent = 'Ürünler yükleniyor…';

      const requestedUrl = this.nextUrl;

      try {
        const response = await fetch(requestedUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = parsedDocument.querySelector(SELECTORS.grid);
        if (!incomingGrid) throw new Error('Yeni ürün listesi bulunamadı.');

        const fragment = document.createDocumentFragment();
        [...incomingGrid.children].forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          fragment.appendChild(item);
        });

        this.grid.appendChild(fragment);
        this.nextUrl = this.findNextUrl(parsedDocument, requestedUrl);
        this.hideNativePagination();

        document.dispatchEvent(new CustomEvent('dawn:infinite-scroll:loaded', {
          detail: { loadedProducts: this.loadedCount, nextUrl: this.nextUrl }
        }));

        this.saveState();
        if (!restoring && typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger();
        }
      } catch (error) {
        console.error('[Infinite Scroll]', error);
        if (this.status) this.status.textContent = 'Ürünler yüklenemedi. Tekrar deneyin.';
        if (this.button) this.button.hidden = false;
        this.disconnectObserver();
      } finally {
        this.loading = false;
        this.controls?.classList.remove('is-loading');
        if (this.button) this.button.disabled = false;
        this.updateControls();
        if (!this.autoStopped && !this.restoreInProgress) this.createObserver();
      }
    }

    handleClick(event) {
      const button = event.target.closest(SELECTORS.button);
      if (!button) return;
      event.preventDefault();
      this.loadNextPage({ automatic: false });
    }

    handleProductClick(event) {
      if (event.target.closest('#product-grid a[href]')) this.saveState();
    }

    saveState() {
      if (!this.enabled || !this.grid) return;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          itemCount: this.loadedCount,
          scrollY: window.scrollY,
          savedAt: Date.now()
        }));
      } catch (error) {
        console.warn('[Infinite Scroll] Konum kaydedilemedi.', error);
      }
    }

    async restoreState() {
      let savedState = null;
      try { savedState = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); }
      catch (error) { savedState = null; }

      const isFresh = savedState && Date.now() - savedState.savedAt < 30 * 60 * 1000;
      const targetCount = Number(savedState?.itemCount || 0);

      if (!isFresh || targetCount <= this.loadedCount) {
        this.createObserver();
        return;
      }

      this.restoreInProgress = true;
      this.disconnectObserver();

      while (this.nextUrl && this.loadedCount < targetCount) {
        await this.loadNextPage({ automatic: false, restoring: true });
      }

      this.restoreInProgress = false;
      this.updateControls();
      this.createObserver();

      requestAnimationFrame(() => {
        window.scrollTo({ top: Number(savedState.scrollY || 0), behavior: 'auto' });
      });
    }
  }

  const start = () => {
    if (!window.dawnHybridInfiniteScroll) {
      window.dawnHybridInfiniteScroll = new DawnHybridInfiniteScroll();
    } else {
      window.dawnHybridInfiniteScroll.init({ allowRestore: true });
    }
  };

  document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', start);

  document.addEventListener('dawn:product-grid:updated', () => {
    if (window.dawnHybridInfiniteScroll) window.dawnHybridInfiniteScroll.reset();
    else start();
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) start();
  });
})();
